import { useCurrentAccount } from '@gripp/shared-logic';
import {
  Colors,
  getMediaTitleForWidth,
  Icon,
  NavigationButton,
} from '@gripp/shared-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMenuPermission } from '../hooks/useMenuPermision';
import { IMenu } from './menuItems';

const SIDEBAR_EXPANDED_WIDTH = 144;
const SIDEBAR_COLLAPSED_WIDTH = 48;
const TRANSITION_DURATION = '0.3s';
const TRANSITION_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

export const Menu = () => {
  const { t } = useTranslation();
  const menuList = useMenuPermission()();
  const navigate = useNavigate();
  const location = useLocation();
  const { account } = useCurrentAccount();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const toggleSidebar = () => {
    setIsCollapsed((prev) => !prev);
  };

  const isActiveRoute = (route: string) => {
    return location.pathname.startsWith(route);
  };

  const renderMenuItem = (link: IMenu, index: number) => {
    if (link.component) {
      return (
        <View
          key={index}
          style={isCollapsed ? styles.menuItemCollapsed : styles.menuItem}
        >
          <link.component {...link} collapsed={isCollapsed} />
        </View>
      );
    }

    return (
      <View
        key={index}
        style={isCollapsed ? styles.menuItemCollapsed : styles.menuItem}
      >
        <NavigationButton
          key={index}
          icon={link.icon}
          text={t(link.titleKey)}
          active={isActiveRoute(link.route)}
          showIcon={true}
          showText={true}
          mode="light"
          collapsed={isCollapsed}
          onPress={() => navigate(link.route)}
        />
      </View>
    );
  };

  const getCompanyTitle = () => {
    const companyName = account?.workspace?.name || t('topNav.myHistory');
    const maxWidth = SIDEBAR_EXPANDED_WIDTH - 32; // 32px padding
    const adjustedTitle = isCollapsed
      ? companyName.substring(0, 1)
      : getMediaTitleForWidth(companyName, '', maxWidth);

    return (
      <Link to="settings" style={{ textDecoration: 'none' }}>
        <Text style={[styles.companySelectorText]}>{adjustedTitle}</Text>
      </Link>
    );
  };

  return (
    <View
      style={[
        styles.container,
        {
          width: isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH,
          // @ts-ignore - web-specific transition property
          transition: `width ${TRANSITION_DURATION} ${TRANSITION_EASING}`,
        },
      ]}
    >
      <View style={styles.topSection}>
        {/* Company Selector / Logo */}
        <View
          style={[
            styles.companySelector,
            // @ts-ignore - web-specific transition
            { transition: `all ${TRANSITION_DURATION} ${TRANSITION_EASING}` },
          ]}
        >
          <View style={[styles.companySelectorContent]}>
            {getCompanyTitle()}
          </View>
        </View>

        <View
          style={[
            styles.menuList,
            isCollapsed && styles.menuListCollapsed,
            // @ts-ignore - web-specific transition
            { transition: `all ${TRANSITION_DURATION} ${TRANSITION_EASING}` },
          ]}
        >
          <View
            style={[
              isCollapsed && styles.menuItemsCollapsed,
              // @ts-ignore - web-specific transition
              { transition: `all ${TRANSITION_DURATION} ${TRANSITION_EASING}` },
            ]}
          >
            {menuList.map((group, gdx) => {
              // Skip the last group (Account) as it will be rendered separately
              if (gdx === menuList.length - 1) return null;

              return group?.map((link: IMenu, index: number) => {
                return renderMenuItem(link, index);
              });
            })}
          </View>
        </View>

        <View
          style={[
            styles.bottomSection,
            isCollapsed && styles.bottomSectionCollapsed,
            // @ts-ignore - web-specific transition
            { transition: `all ${TRANSITION_DURATION} ${TRANSITION_EASING}` },
          ]}
        >
          {/* Toggle Button */}
          <Pressable
            onPress={toggleSidebar}
            style={({ pressed, hovered }) => [
              styles.toggleButton,
              hovered && styles.toggleButtonHover,
              pressed && styles.toggleButtonPressed,
            ]}
          >
            <Icon
              name={isCollapsed ? 'Chevron Right' : 'Chevron Left'}
              size={16}
              color={Colors.role.textPrimary}
            />
          </Pressable>

          {/* Settings and other bottom items */}
          {menuList[menuList.length - 1]?.map((link: IMenu, index: number) => {
            return renderMenuItem(link, index);
          })}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: Colors.role.canvasRecessed,
    overflow: 'hidden',
  },
  topSection: {
    flex: 1,
    width: '100%',
  },
  bottomSection: {
    width: '100%',
    alignItems: 'flex-start',
    position: 'absolute',
    bottom: 20,
    paddingLeft: 16,
    paddingRight: 0,
    gap: 8,
  },
  bottomSectionCollapsed: {
    paddingLeft: 16,
    paddingRight: 0,
  },
  companySelector: {
    paddingVertical: 16,
    paddingLeft: 16,
    paddingRight: 0,
  },
  companySelectorContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    gap: 8,
  },
  companySelectorText: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.role.textStrong,
    marginLeft: 8,
    marginRight: 'auto',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
  },
  menuList: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    height: '80%',
    justifyContent: 'center',
    paddingVertical: 16,
    paddingLeft: 16,
    paddingRight: 0,
    gap: 4,
  },
  menuListCollapsed: {
    paddingVertical: 8,
    paddingLeft: 16,
    paddingRight: 0,
  },
  menuItem: {
    marginVertical: 4,
  },
  menuItemCollapsed: {
    marginVertical: 4,
  },
  menuItemsCollapsed: {
    width: '100%',
  },
  toggleButton: {
    width: 32,
    height: 32,
    borderRadius: 100,
    backgroundColor: Colors.role.surfaceSelected,
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    // @ts-ignore - web-specific transition
    transition: 'background-color 0.15s ease-in-out',
  },
  toggleButtonHover: {
    backgroundColor: Colors.role.fillDisabled,
  },
  toggleButtonPressed: {
    backgroundColor: Colors.role.borderStrong,
  },
});
